<template>
  <div>
    <div v-if="!purchaseComplete" @submit.prevent>
      <h2>Add Additional Students</h2>
      <form>
        <div>
          <button
            id="add-participant"
            class="mb-1 tdesk-primary-button"
            @click="showParticipantModal"
          >
            <i class="fas fa-plus" />
            Add Student
          </button>
        </div>

        <div class="w-100">
          <form-errors :errors="formErrors" />

          <div>
            <div v-if="participants.length === 0" class="center-text">
              <div class="tdesk-no-search-results-placeholder w-100">
                <i class="fas fa-graduation-cap no-students-icon" />
                <h3 class="no-students-text">
                  No Students Added
                </h3>
              </div>
            </div>
            <div v-else style="max-height: 80vh; overflow-y: auto;" class="w-100">
              <div
                v-for="(participant, index) in participants"
                :key="participant.email"
                class="enrolled-student-card"
              >
                <div class="card-counter">
                  {{ index + 1 }}
                </div>

                <div class="delete-icon-container">
                  <i
                    :id="'delete-' + index"
                    class="delete-icon fas fa-times"
                    :name="'delete-' + participant.first_name"
                    @click="confirmAndRemoveParticipant(index)"
                  />
                </div>

                <div class="ml-1">
                  <h3 class="main-information">
                    {{ participant.first_name }} {{ participant.last_name }}
                  </h3>
                  <p class="secondary-information">
                    {{ participant.email }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="payment-button-container mt-2 mb-2">
          <button
            id="proceed-payment"
            class="tdesk-primary-button"
            type="button"
            @click="proceedToPayment"
          >
            <i class="fas fa-forward" />
            {{ proceedButtonText }}
          </button>
          <button
            id="cancel-order"
            type="button"
            class="tdesk-tertiary-button"
            @click="cancelOrder"
          >
            <i class="fas fa-times" /> Cancel
          </button>
        </div>
      </form>
      <Modal modal-id="add-participant-modal" modal-title="Add New Student">
        <add-participant-form
          :participants="participants"
          :course-id="courseId"
          :is-partner-course="isPartnerCourse"
          @add-participant="addParticipant"
        />
      </Modal>
      <Modal modal-id="complete-payment-modal" :modal-title="modalTitle">
        <purchase-form
          :participants="participants"
          :organizer="organizer"
          :course-title="courseTitle"
          :course-id="courseId"
          :price-per-enrol="coursePrice"
          :is-partner-course="isPartnerCourse"
          :partner-id="partnerId"
          :stripe-api-key="stripeApiKey"
          :stripe-account-id="stripeAccountId"
          :current-user-information="currentUserInformation"
          @paymentComplete="paymentComplete"
        />
      </Modal>
    </div>
    <div v-else>
      <order-summary
        :current-user-information="currentUserInformation"
        :organizer="organizer"
        :is-free-course="isFreeCourse"
      />
    </div>
  </div>
</template>

<script>
import Modal from '../../../components/modal.vue';
import AddParticipantForm from './AddParticipantForm.vue';
import FormErrors from "../../../components/form_errors.vue";
import PurchaseForm from './PurchaseForm.vue';
import OrderSummary from './OrderSummary.vue';
import {getBulkEligibilityCheckPath} from "./helpers/courseEligibility";

const toastr = window.toastr;
export default {
  components: { FormErrors, AddParticipantForm, PurchaseForm, OrderSummary, Modal },
  props: {
    organizer: { type: Object, required: true },
    courseTitle: { type: String, required: true },
    coursePrice: { type: Number, required: true },
    courseId: { type: Number, required: true },
    isPartnerCourse: { type: Boolean, required: true },
    partnerId: { type: Number, required: false, default: null },
    stripeApiKey: { type: String, required: true },
    stripeAccountId: { type: String, required: false, default: null },
    currentUserInformation: { type: String, required: true },
  },

  data() {
    return {
      participants: [],
      formErrors: [],
      purchaseComplete: false,
      newStudent: {
        first_name: '',
        last_name: '',
        email: '',
      },
    };
  },

  computed: {
    isFreeCourse() {
      return this.coursePrice <= 0;
    },
    modalTitle() {
      return this.isFreeCourse ? 'Complete' : 'Complete Payment';
    },
    proceedButtonText() {
      return this.isFreeCourse ? 'Proceed' : 'Proceed To Payment';
    },
  },

  created() {
    if (this.organizer.enrolling_self) {this.addOrganizerToParticipants();}
  },

  methods: {
    addOrganizerToParticipants() {
      const organizerParticipant = {
        first_name: this.organizer.first_name,
        last_name: this.organizer.last_name,
        email: this.organizer.email,
      };
      this.participants.push(organizerParticipant);
    },

    addParticipant(participant) {
      this.participants.push(participant);
      this.hideParticipantModal();
      this.formErrors = [];
      toastr.success('Student added successfully');
    },

    showParticipantModal: () => $('#add-participant-modal').modal('show'),
    hideParticipantModal: () => $('#add-participant-modal').modal('hide'),

    showPaymentCompletionModal: () => $('#complete-payment-modal').modal('show'),
    hidePaymentCompletionModal: () => $('#complete-payment-modal').modal('hide'),

    confirmAndRemoveParticipant(index) {
      if (window.confirm("Are you sure you want to remove this participant?")) {
        this.participants.splice(index, 1);
        toastr.success('Student removed successfully');
      }
    },

    cancelOrder() {
      window.location.reload();
    },

    paymentComplete() {
      this.purchaseComplete = true;
      this.hidePaymentCompletionModal();
    },

    updateParticipantName(userWithError) {
      const participantIndex = this.participants.findIndex(
        participant => participant.email === userWithError.user_details.email,
      );

      if (participantIndex !== -1) {
        this.$set(this.participants, participantIndex, {
          ...this.participants[participantIndex],
          first_name: userWithError.user_details.first_name,
          last_name: userWithError.user_details.last_name,
        });
      }
    },

    confirmAllUserEligibility() {
      const currentUrlParams = new URLSearchParams(window.location.search);
      // cid needed as this is how admin lms courses map to a course as opposed to /course_id/
      const cid = currentUrlParams.get('cid');
      const usersData = this.participants.map(participant => ({
        email: participant.email,
        first_name: participant.first_name,
        last_name: participant.last_name,
      }));
      const paramsObj = {
        cid: cid,
        users: JSON.stringify(usersData),
        partner_id: this.partnerId,
        lms_course_id: this.courseId,
      };
      const bulkEligibilityCheckPath = getBulkEligibilityCheckPath({isPartnerCourse: this.isPartnerCourse, params: paramsObj});

      $.ajax({
        url: bulkEligibilityCheckPath,
        type: 'GET',
        dataType: 'json',
        contentType: 'application/json',
        success: function(response) {
          if (response.all_eligible) {
            this.formErrors = [];
            this.showPaymentCompletionModal();
          } else {
            const usersWithIncorrectNameDetails = response.users_with_errors.filter(user => user.error_type === 'incorrect_name_details');
            const errorMessages = response.users_with_errors.map(user => user.message);

            usersWithIncorrectNameDetails.forEach(this.updateParticipantName);
            this.formErrors = errorMessages;
          }
        }.bind(this),
        error: function() {
          this.formErrors = ['An error occurred. Please try again or contact support if this issue persists'];
        }.bind(this),
      });
    },

    proceedToPayment() {
      if (this.participants.length === 0) {
        this.formErrors = ['You must be enrolling at least one student to continue'];
      } else {
        this.confirmAllUserEligibility();
      }
    },
  },
};
</script>

<style scoped>
.center-text {
  text-align: center;
}

.payment-button-container {
  margin-top: auto;
  margin-bottom: 16px;
}
.no-students-icon {
  font-size: 24px;
  /* $search-select-text-color */
  color: #999;
}

.no-students-text {
  margin-top: 8px;
  /* $unit-panel-collapse-arrow */
  color: #555;
}
</style>
